import { Component, ReactNode } from 'react';

import { captureException } from '@/config/observability';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import { GridBackground } from '@/components/core/common/GridBackground';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. When omitted, renders a full-page
   *  `ErrorState` with a "Reload Page" primary action. */
  fallback?: ReactNode;
  /** Called once when the boundary catches, after it is reported. For hosts
   *  that must settle their own state when the subtree dies (e.g. save what
   *  the crashed subtree had produced) rather than just render a fallback. */
  onError?: (error: Error) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    captureException(error, { componentStack: errorInfo?.componentStack });
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Root-level fallback: this boundary sits above AppLayout's
      // GridBackground, so when it triggers the textured backdrop
      // hasn't mounted. Render our own GridBackground here so the
      // fallback shows the same brand surface AppContent normally
      // provides. `centerContent` wraps children in a flex parent
      // with `min-h-screen items-center justify-center`, which is
      // what actually centers the card vertically - a plain
      // `min-h-screen` div doesn't give `PanelFallback`'s `h-full`
      // anything to resolve against, so the card would sit at the
      // top instead.
      return (
        <GridBackground className='min-h-screen bg-ground' centerContent>
          <PanelFallback
            variant='page'
            title='Something went wrong'
            description='An unexpected error occurred. Reloading the page often fixes it.'
            primary={{
              label: 'Reload Page',
              onClick: () => window.location.reload(),
            }}
          />
        </GridBackground>
      );
    }

    return this.props.children;
  }
}
